// ============================================================================
// DATA URL TO BLOB â Shared utility for CSP-safe base64 â Blob conversion
// ----------------------------------------------------------------------------
// Manual atob() + Uint8Array decode. NEVER use fetch() on data URLs â it
// fails under Content Security Policy restrictions in many browsers.
//
// This is the single source of truth. All consumers must import from here.
// Previously duplicated in: photo-blob-store.js, file-upload-service.js,
// file-system.js (Ã2 inline), project-projector.js.
// ============================================================================

/**
 * Convert a base64 data URL (or raw base64 string) to a Blob.
 * CSP-safe: uses manual atob() + Uint8Array, NOT fetch().
 *
 * Accepts both formats:
 *   - Data URL: "data:image/jpeg;base64,/9j/4AAQ..." (MIME extracted from header)
 *   - Raw base64: "/9j/4AAQ..." (uses fallbackMime)
 *
 * @param {string} dataUrl - data URL or raw base64 string
 * @param {string} [fallbackMime='application/octet-stream'] - MIME type when input has no data URI header
 * @returns {Promise<Blob>}
 */
export async function dataUrlToBlob(dataUrl: string, fallbackMime: string = 'application/octet-stream'): Promise<Blob> {
    let base64: string;
    let mime: string;
    if (dataUrl.includes(',')) {
        const [header, b64] = dataUrl.split(',');
        // `header` and `b64` are provably present: String.split(',') on a
        // string containing ',' yields at least two elements. The `?? ''`
        // fallbacks keep runtime byte-identical to the .js original while
        // satisfying noUncheckedIndexedAccess; the capture group is non-null
        // when `mimeMatch` is truthy.
        const mimeMatch = (header ?? '').match(/data:([^;]+)/);
        mime = mimeMatch ? mimeMatch[1]! : fallbackMime;
        base64 = b64 ?? '';
    } else {
        // Raw base64 string â no data URI prefix
        base64 = dataUrl;
        mime = fallbackMime;
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}
